// Theme › Texts — sign-in, sign-up and reset-password forms and the header
// Log in / Sign up buttons (runtime `authTexts`, v0.20; ott-cms
// lib/sites/auth-texts.ts). Same override model as `broadcastTexts`: a flat
// key → text map, empty = the shipped wording, `{}` clears every override.
// Both templates read it; only the two subtitles differ per template.

export const AUTH_TEXT_GROUPS = [
	{ id: 'signin', title: 'Sign in', description: 'The sign-in page.' },
	{ id: 'signup', title: 'Sign up', description: 'The create-account page.' },
	{ id: 'reset', title: 'Reset password', description: 'The forgot-password page.' },
	{ id: 'header', title: 'Header', description: 'The Log in / Sign up buttons at the top of every page.' },
]

/**
 * `defaults.catalog` is the shipped English wording; `defaults.broadcast`
 * only where the Broadcast template words it differently. `legacy` = the
 * older single-label setting that still shows when no override is set
 * (`legacyDefault` = the value the runtime treats as "use the shipped wording").
 */
export const AUTH_TEXT_FIELDS = [
	{ key: 'signInTitle', group: 'signin', where: 'Heading', defaults: { catalog: 'Welcome back' } },
	{ key: 'signInSubtitle', group: 'signin', where: 'Under the heading', defaults: { catalog: 'Sign in to keep watching.', broadcast: 'Sign in to watch services and recordings.' } },
	{ key: 'emailLabel', group: 'signin', where: 'Field label · sign in and sign up', defaults: { catalog: 'Email' } },
	{ key: 'emailPlaceholder', group: 'signin', where: 'Inside the email field · sign in and sign up', defaults: { catalog: 'you@example.com' } },
	{ key: 'passwordLabel', group: 'signin', where: 'Field label · sign in and sign up', defaults: { catalog: 'Password' } },
	{ key: 'forgotPasswordLink', group: 'signin', where: 'Link next to the password', defaults: { catalog: 'Forgot password?' } },
	{ key: 'signInButton', group: 'signin', where: 'Submit button', defaults: { catalog: 'Sign in' }, legacy: 'loginPageLoginText', legacyDefault: 'login' },
	{ key: 'noAccountPrompt', group: 'signin', where: 'Under the button', defaults: { catalog: "Don't have an account?" } },
	{ key: 'signUpLink', group: 'signin', where: 'Link under the button', defaults: { catalog: 'Sign up' }, legacy: 'loginPageSignupText', legacyDefault: 'signup' },
	{ key: 'backToHomepage', group: 'signin', where: 'Back link', defaults: { catalog: 'Back to homepage' }, legacy: 'loginPageBackToHomepageText' },
	{ key: 'signUpTitle', group: 'signup', where: 'Heading', defaults: { catalog: 'Create your account' } },
	{ key: 'signUpSubtitle', group: 'signup', where: 'Under the heading', defaults: { catalog: 'Create an account to watch and buy on this site.', broadcast: 'Create a free account to watch services and recordings.' } },
	{ key: 'fullNameLabel', group: 'signup', where: 'Field label', defaults: { catalog: 'Full name' } },
	{ key: 'passwordHint', group: 'signup', where: 'Under the password field', defaults: { catalog: 'At least 8 characters' } },
	{ key: 'signUpButton', group: 'signup', where: 'Submit button', defaults: { catalog: 'Create account' }, legacy: 'signupPageSignupText', legacyDefault: 'signup' },
	{ key: 'haveAccountPrompt', group: 'signup', where: 'Under the button', defaults: { catalog: 'Already have an account?' } },
	{ key: 'signInLink', group: 'signup', where: 'Link under the button', defaults: { catalog: 'Sign in' } },
	{ key: 'backToSignIn', group: 'signup', where: 'Back link', defaults: { catalog: 'Back to sign in' } },
	{ key: 'resetTitle', group: 'reset', where: 'Heading', defaults: { catalog: 'Reset password' } },
	{ key: 'resetSubtitle', group: 'reset', where: 'Under the heading', defaults: { catalog: "Enter the email associated with your account and we'll send an email with instructions to reset your password." } },
	{ key: 'resetEmailPlaceholder', group: 'reset', where: 'Inside the email field', defaults: { catalog: 'Enter your email' } },
	{ key: 'resetButton', group: 'reset', where: 'Submit button', defaults: { catalog: 'Send' } },
	{ key: 'backToLogin', group: 'reset', where: 'Back link · also on “Check your email”', defaults: { catalog: 'Back to Login' } },
	{ key: 'headerLogIn', group: 'header', where: 'Header button', defaults: { catalog: 'Log in' }, legacy: 'homepageLoginText', legacyDefault: 'login' },
	{ key: 'headerSignUp', group: 'header', where: 'Header button', defaults: { catalog: 'Sign up' }, legacy: 'homepageSignupText', legacyDefault: 'signup' },
]
export const AUTH_TEXT_MAX = 200

/** Display-card keys the auth texts replace in Theme › Texts once the runtime accepts `authTexts`. */
export const LEGACY_AUTH_TEXT_KEYS = AUTH_TEXT_FIELDS.filter((f) => f.legacy && f.legacy !== 'loginPageBackToHomepageText').map((f) => f.legacy)

const squash = (v) => String(v || '').toLowerCase().replace(/\s/g, '')

/**
 * What viewers see for a key without an override: the older single-label
 * setting when it is set to something other than its "default" value
 * (the runtime's own check), else the shipped wording for the template.
 */
export function defaultAuthText(field, template, settings = {}) {
	const legacy = field.legacy ? String(settings?.[field.legacy] || '').trim() : ''
	if (legacy && (!field.legacyDefault || squash(legacy) !== field.legacyDefault)) return legacy
	return (template === 'broadcast' && field.defaults.broadcast) || field.defaults.catalog
}

/** `authTexts` → known keys with a trimmed, single-line, non-empty value. */
export function normalizeAuthTexts(value) {
	const out = {}
	if (!value || typeof value !== 'object' || Array.isArray(value)) return out
	for (const f of AUTH_TEXT_FIELDS) {
		const v = value[f.key]
		if (typeof v !== 'string') continue
		// eslint-disable-next-line no-control-regex
		const text = v.replace(/[\u0000-\u001F\u007F]/g, ' ').trim()
		if (text) out[f.key] = text
	}
	return out
}
